import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IUser } from 'models/auth';
import { DEFAULT_SETTINGS, Settings } from '@/models/settings';
import {
  defaultUser,
  PageLoadingContext,
  ProfileContext,
  SettingsContext,
  ThemeContext,
  UserContext,
} from '@/state/contexts';

import {
  useGetSettingsQuery,
  usePatchSettingMutation,
} from '@/state/slices/setting';

const SETTINGS_STORAGE_KEY = 'userSettings';

function normalizeSettingsShape(
  settings: Partial<Settings> & { show_addendum_modal?: boolean }
): Partial<Settings> {
  if (settings.showAddendumModal !== undefined) {
    return settings;
  }

  if (settings.show_addendum_modal !== undefined) {
    return {
      ...settings,
      showAddendumModal: settings.show_addendum_modal,
    };
  }

  return settings;
}

function loadCachedSettings(): Settings {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (raw) {
      const parsed = normalizeSettingsShape(
        JSON.parse(raw) as Partial<Settings> & { show_addendum_modal?: boolean }
      );
      // Merge with defaults so older cached objects get new fields (e.g.
      // rememberDeviceEnabled) — required for MFA "Remember this device" on /login.
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch {
    /* ignore */
  }
  // Respect existing dark theme preference from ThemeProvider if set
  try {
    const darkThemeRaw = localStorage.getItem('darkTheme');
    if (darkThemeRaw != null) {
      return {
        ...DEFAULT_SETTINGS,
        darkMode: JSON.parse(darkThemeRaw) as boolean,
      };
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_SETTINGS;
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [darkTheme, setDarkThemeState] = useState<boolean>(() => {
    const raw = localStorage.getItem('darkTheme');
    if (raw == null) return true;
    try {
      return JSON.parse(raw) as boolean;
    } catch {
      return true;
    }
  });

  const setDarkTheme = useCallback((value: boolean) => {
    setDarkThemeState(value);
    localStorage.setItem('darkTheme', JSON.stringify(value));
  }, []);

  // Sync dark mode changes made in other tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === 'darkTheme' && e.newValue !== null) {
        try {
          setDarkThemeState(JSON.parse(e.newValue) as boolean);
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const themeValue = useMemo(
    () => ({ darkTheme, setDarkTheme }),
    [darkTheme, setDarkTheme]
  );

  return (
    <ThemeContext.Provider value={themeValue}>{children}</ThemeContext.Provider>
  );
};

function getStoredUser(): IUser | null {
  try {
    const raw = localStorage.getItem('user');
    if (raw) return JSON.parse(raw) as IUser;
  } catch {
    /* ignore */
  }
  return null;
}

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<IUser | null>(getStoredUser);

  const userValue = useMemo(() => ({ user, setUser }), [user]);

  return (
    <UserContext.Provider value={userValue}>{children}</UserContext.Provider>
  );
};

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useContext(UserContext);
  const { setDarkTheme } = useContext(ThemeContext);

  const isFullyAuthenticated =
    (!!user?.id || !!user?.email) && !!user?.mfaEnabled;

  const { data: settingsResponse, isLoading } = useGetSettingsQuery(undefined, {
    skip: !isFullyAuthenticated,
  });

  const remoteSettings = settingsResponse?.results?.[0] ?? null;
  const [localSettings, setLocalSettings] = useState<Settings>(() =>
    loadCachedSettings()
  );

  const [patchSetting] = usePatchSettingMutation();

  // Sync settings changes made in other tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === SETTINGS_STORAGE_KEY && e.newValue !== null) {
        try {
          const parsed = normalizeSettingsShape(
            JSON.parse(e.newValue) as Partial<Settings> & {
              show_addendum_modal?: boolean;
            }
          );
          const next = {
            ...DEFAULT_SETTINGS,
            ...parsed,
          };
          setLocalSettings(next);
          setDarkTheme(next.darkMode);
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [setDarkTheme]);

  // Seed local state from remote settings on initial fetch and user switch.
  // Always overwrite local with remote to pick up backend-side changes
  // (e.g. new fields like defaultWorksheet added after initial cache).
  const seededUuidRef = useRef<string | null>(null);
  useEffect(() => {
    if (!remoteSettings) return;
    if (seededUuidRef.current === remoteSettings.uuid) return;
    seededUuidRef.current = remoteSettings.uuid;
    const mergedSettings = { ...DEFAULT_SETTINGS, ...remoteSettings };
    setLocalSettings(mergedSettings);
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(mergedSettings));
    setDarkTheme(mergedSettings.darkMode);
  }, [remoteSettings, setDarkTheme]);

  const setSettings = useCallback(
    (partial: Partial<Settings>) => {
      setLocalSettings((prev) => {
        const next = { ...prev, ...partial };
        localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(next));
        // PATCH using merged `next.uuid` — not closure `localSettings.uuid` (stale / empty on login).
        if (next.uuid) {
          queueMicrotask(() => {
            patchSetting({ uuid: next.uuid, data: partial });
          });
        }
        return next;
      });
    },
    [patchSetting]
  );

  const value = useMemo(
    () => ({
      settings: localSettings,
      setSettings,
      isLoading: isFullyAuthenticated && isLoading,
    }),
    [localSettings, setSettings, isLoading, isFullyAuthenticated]
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedProfile, setSelectedProfile] = useState<IUser>(defaultUser);

  const profileValue = useMemo(
    () => ({ selectedProfile, setSelectedProfile }),
    [selectedProfile]
  );

  return (
    <ProfileContext.Provider value={profileValue}>
      {children}
    </ProfileContext.Provider>
  );
};

export const PageLoadingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isPageLoading, setPageLoading] = useState(false);

  const value = useMemo(
    () => ({ isPageLoading, setPageLoading }),
    [isPageLoading]
  );

  return (
    <PageLoadingContext.Provider value={value}>
      {children}
    </PageLoadingContext.Provider>
  );
};
